import { X, Trophy, Star, Crown, Target, Flag } from "lucide-react";
import { isGolfFormat, isGolfPickem, isSeasonFormat } from "@/lib/tournamentFormat";

/**
 * Format-aware rules modal. Routes to a per-game-type explainer
 * (iter86.31.4 — user guidance, Message 476):
 *   1. Golf — Lowest Average Score
 *   2. Golf — Position Draft
 *   3. Soccer Group + Knockout (no "Currently supported" header — starts
 *      at Drafted Team Score)
 *   4. Knockout-only (World Cup KO, March Madness, playoffs)
 *   5. Season (round-robin — PL / La Liga / Serie A / Super Draft)
 *   6. Custom (admin-defined scoring)
 *
 * Reads tournament.scoring / tournament.golf.scoring / tournament.stages so
 * values stay accurate as the league admin tunes points. Trusts the parent's
 * `tournament` prop directly (iter63z3) — the parent keeps it fresh via WS.
 */
export default function RulesModal({ onClose, tournament }) {
    const template = tournament?.template || "world_cup";
    const format = tournament?.format || "group_knockout";
    const scoring = tournament?.scoring || {};
    const stageKeys = new Set((tournament?.stages || []).map(s => s.key));
    const isCustom = template === "custom";
    const golfPickem = isGolfPickem(tournament);
    const golfDraft = isGolfFormat(tournament) && !golfPickem;
    const isGolf = golfPickem || golfDraft;
    const isSeason = !isCustom && !isGolf && isSeasonFormat(tournament);
    const isKO = !isCustom && !isGolf && !isSeason && format === "knockout_only";
    const golfScoring = tournament?.golf?.scoring || {};

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()}
                 className="brutal-card p-6 lg:p-8 max-w-2xl w-full max-h-[90dvh] overflow-y-auto"
                 data-testid="rules-modal">
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-cyber border-2 border-black shadow-brutal flex items-center justify-center">
                            <Trophy className="w-5 h-5 text-black" strokeWidth={2.5} />
                        </div>
                        <h2 className="font-display font-black text-2xl">Point Accumulation & Scoring</h2>
                    </div>
                    <button onClick={onClose} className="brutal-btn-ghost p-2" data-testid="close-rules-btn">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="space-y-5 text-sm">
                    {golfPickem && <GolfPickemRules golfScoring={golfScoring} potRules={tournament?.golf?.pot_rules} />}
                    {golfDraft && <GolfDraftRules golfScoring={golfScoring} />}
                    {isSeason && <SeasonRules scoring={scoring} tournament={tournament} />}
                    {isKO && <KnockoutOnlyRules scoring={scoring} stageKeys={stageKeys} />}
                    {!isGolf && !isSeason && !isKO && !isCustom && (
                        <GroupKnockoutRules scoring={scoring} stageKeys={stageKeys} tournament={tournament} />
                    )}
                    {isCustom && <CustomRules format={format} scoring={scoring} tournament={tournament} stageKeys={stageKeys} />}

                    {/* iter35d / iter86.31.4 — Power Pick rules, format-aware copy */}
                    {tournament?.power_pick_enabled && (
                        <PowerPickSection
                            tournament={tournament}
                            golfPickem={golfPickem}
                            golfDraft={golfDraft}
                            isSeason={isSeason}
                            golfScoring={golfScoring}
                        />
                    )}

                    {/* iter86.43.2 — Matchday Boost rules (soccer seasons). */}
                    {isSeason && tournament?.matchday_boost?.enabled && (
                        <MatchdayBoostSection spreadMode={tournament?.matchday_boost?.scoring_mode === "spread"} />
                    )}

                    <section className="border-t-2 border-[#222] pt-4">
                        <h3 className="font-display font-extrabold text-base uppercase tracking-tight mb-2">Notes</h3>
                        <ul className="space-y-1 text-zinc-400 text-xs">
                            {isGolf ? (
                                <>
                                    <li>• Live golfer scores sync automatically every ~15 minutes during play — no refresh needed.</li>
                                    <li>• Picks, Power Pick and scoring settings <b>lock when Round 1 tees off</b>.</li>
                                    {golfDraft && <li>• League admins can tune the point values in <b>Admin Panel → Scoring</b> (before Round 1).</li>}
                                </>
                            ) : (
                                <>
                                    <li>• League admins can customize all point values in <b>Admin Panel → Scoring</b>.</li>
                                    <li>• Match results can be edited or removed from <b>Admin Panel → Enter Match</b>.</li>
                                    <li>• Leaderboard updates via WebSocket — no refresh needed.</li>
                                </>
                            )}
                        </ul>
                    </section>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* 1. Golf — Lowest Average Score                                      */
/* ------------------------------------------------------------------ */
function GolfPickemRules({ golfScoring, potRules }) {
    const champStrokes = golfScoring.champion_pick_bonus_strokes ?? 4;
    const NUM_WORD = { 4: "Four", 5: "Five", 6: "Six", 7: "Seven", 8: "Eight", 9: "Nine", 10: "Ten" };
    const rules = potRules?.length ? potRules : [
        { pot: 1, rank_from: 1, rank_to: 5, picks: 1 },
        { pot: 2, rank_from: 6, rank_to: 15, picks: 2 },
        { pot: 3, rank_from: 16, rank_to: 30, picks: 3 },
        { pot: 4, rank_from: 31, rank_to: 9999, picks: 2 },
    ];
    const totalPicks = rules.reduce((s, r) => s + (r.picks || 0), 0);
    const word = NUM_WORD[totalPicks] || String(totalPicks);
    const range = (r) => (r.rank_to >= 999 ? `${r.rank_from}+ · rest of field` : `${r.rank_from}–${r.rank_to}`);
    return (
        <>
            <section data-testid="rules-golf-pickem-section">
                <p className="text-[10px] uppercase tracking-widest text-cyber font-bold">Golf</p>
                <h3 className="font-display font-extrabold text-xl mb-1">Lowest Average Score</h3>
                <p className="text-zinc-400 text-xs">
                    Your score is <b className="text-cyber">the average strokes per completed round of golf across all your picks</b>. Lowest average wins.
                </p>
            </section>

            <section>
                <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-cyber" /> Your {word} Golfers
                </h3>
                <ul className="space-y-1 text-zinc-300 text-xs">
                    <li>• Pick {rules.map((r, i) => (
                        <span key={r.pot}>{i > 0 && ", "}<b>{r.picks}</b> from Pot {r.pot} (world ranks {range(r)})</span>
                    ))}.</li>
                    <li>• Crewmates <b>can</b> pick the same golfers — every card is scored independently.</li>
                    <li>• Picks lock at Round 1 tee-off; change them as often as you like before then.</li>
                </ul>
            </section>

            <section>
                <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                    <Flag className="w-4 h-4 text-volt" /> How the average works
                </h3>
                <ul className="space-y-1 text-zinc-300 text-xs" data-testid="rules-golf-avg-list">
                    <li>• Total strokes ÷ rounds <b>actually played</b> across your golfers.</li>
                    <li>• Missed the cut → only their two rounds count. Withdrawal → whatever rounds they completed.</li>
                    <li>• A golfer having a nightmare weekend drags your average UP — choose durability, not just star power.</li>
                </ul>
            </section>

            <section>
                <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                    <Crown className="w-4 h-4 text-cyber" /> Champions Pick
                </h3>
                <p className="text-zinc-400 text-xs" data-testid="rules-golf-champion-pick">
                    Pick the event winner. If they lift the trophy you get <b className="text-cyber">−{champStrokes} strokes</b> off
                    your total. Locks <b>before Round 3</b> — no decay, just commit before the weekend.
                </p>
            </section>
        </>
    );
}

/* ------------------------------------------------------------------ */
/* 2. Golf — Position Draft                                            */
/* ------------------------------------------------------------------ */
function GolfDraftRules({ golfScoring }) {
    const cutPts = golfScoring.make_cut_points ?? 3;
    const champPts = golfScoring.champion_pick_points ?? 10;
    const posPts = golfScoring.position_points || {
        "1": 20, "2": 17, "3": 15,
        ...Object.fromEntries(Array.from({ length: 7 }, (_, i) => [String(i + 4), 10])),
        ...Object.fromEntries(Array.from({ length: 10 }, (_, i) => [String(i + 11), 5])),
    };
    // Compress consecutive equal-value positions into ranges: 4º–10º:10
    const posEntries = Object.entries(posPts)
        .map(([k, v]) => [Number(k), v])
        .sort((a, b) => a[0] - b[0]);
    const posTiers = [];
    for (const [pos, val] of posEntries) {
        const last = posTiers[posTiers.length - 1];
        if (last && last.val === val && last.to === pos - 1) last.to = pos;
        else posTiers.push({ from: pos, to: pos, val });
    }
    const posSummary = posTiers
        .map(t => `${t.from}º${t.to !== t.from ? `–${t.to}º` : ""}:${t.val}`)
        .join(" · ");
    return (
        <>
            <section data-testid="rules-golf-draft-section">
                <p className="text-[10px] uppercase tracking-widest text-cyber font-bold">Golf</p>
                <h3 className="font-display font-extrabold text-xl mb-1">Position Draft</h3>
                <p className="text-zinc-400 text-xs">
                    Drafted golfers accumulate points by <b className="text-cyber">making the cut</b> and by
                    <b className="text-cyber"> finishing in the top 20</b> at the end of the weekend. Highest total wins.
                </p>
            </section>

            <section>
                <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-cyber" /> Drafted Golfers Score
                </h3>
                <ol className="space-y-1 list-decimal list-inside text-zinc-300 text-xs">
                    <li>Each crewmate gets an equal set of golfers through the auto-snake draft (seeded by world ranking).</li>
                    <li>Score grows as your golfers survive the cut and climb the leaderboard.</li>
                </ol>
            </section>

            <section>
                <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-volt" /> Points
                </h3>
                <ul className="space-y-1 text-zinc-300 text-xs" data-testid="rules-golf-draft-points">
                    <li>• ✂️ <b>Make the cut</b>: <b className="text-cyber">+{cutPts} pts</b></li>
                    <li>• 🏁 <b>Final position</b>:{" "}
                        <b className="text-cyber font-mono">{posSummary}</b>
                        {" "}<span className="text-zinc-500">(ties count the position number)</span>
                    </li>
                </ul>
            </section>

            <section>
                <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                    <Crown className="w-4 h-4 text-cyber" /> Champions Pick
                </h3>
                <p className="text-zinc-400 text-xs" data-testid="rules-golf-draft-champion-pick">
                    Selecting the correct Champions Pick (the event winner) gets you an extra
                    <b className="text-cyber"> +{champPts} pts</b>. Locks <b>before Round 3</b>.
                </p>
            </section>
        </>
    );
}

/* ------------------------------------------------------------------ */
/* 5. Season (round-robin) — PL / La Liga / Serie A / Super Draft      */
/* ------------------------------------------------------------------ */
function SeasonRules({ scoring, tournament }) {
    const win = scoring.win_points ?? 3;
    const draw = scoring.draw_points ?? 1;
    const isNfl = tournament?.template === "nfl_season_api";
    const champBonus = tournament?.pickem?.champions_pick_bonus ?? 15;
    return (
        <>
            <section data-testid="rules-season-section">
                <p className="text-[10px] uppercase tracking-widest text-cyber font-bold">Season format</p>
                <h3 className="font-display font-extrabold text-xl mb-1">{tournament?.name || "Season"}</h3>
                <p className="text-zinc-400 text-xs">
                    <b className="text-cyber">Wins & ties get you points</b> across the whole season — every {isNfl ? "game your eight teams" : "fixture your drafted teams"} play{isNfl ? "" : ""} counts.
                </p>
            </section>

            <section>
                <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-cyber" /> {isNfl ? "Your Eight — Pot Picks" : "Drafted Team(s) Score"}
                </h3>
                <ol className="space-y-1 list-decimal list-inside text-zinc-300 text-xs">
                    {isNfl ? (
                        <>
                            <li>Pick 2 teams from each of the 4 pots — <b>Contenders · Dark horses · Longshots · Moonshots</b>, tiered by preseason Super Bowl odds.</li>
                            <li>Score grows with every result those 8 teams post, all 18 weeks long.</li>
                        </>
                    ) : (
                        <>
                            <li>Each crewmate ends up with an equal set of teams through the draft (initiated by the Admin).</li>
                            <li>Score grows with every result those teams post, all season long.</li>
                        </>
                    )}
                </ol>
            </section>

            <section>
                <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-volt" /> {isNfl ? "Every game" : "Every fixture"}
                </h3>
                <ul className="space-y-1 text-zinc-300" data-testid="rules-scoring-group-list">
                    <li>• <b>Win</b>: <b className="text-cyber">+{win} pts</b></li>
                    <li>• <b>{isNfl ? "Tie" : "Draw / Tie"}</b>: <b className="text-cyber">+{draw} pt{draw === 1 ? "" : "s"}</b></li>
                    <li>• <b>Loss</b>: <b className="text-cyber">0 pts</b></li>
                </ul>
            </section>

            <section>
                <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                    <Crown className="w-4 h-4 text-cyber" /> Champion
                </h3>
                {isNfl ? (
                    <p className="text-zinc-400 text-xs" data-testid="rules-nfl-champions-pick">
                        Whoever leads the leaderboard when the regular season ends is the champion. Plus the <b className="text-cyber">Champions Pick</b>: nominate the team you think finishes with the <b>best regular-season record</b> — banks <b className="text-cyber">+{champBonus} pts</b> at season end if you're right (record ties all pay).
                    </p>
                ) : (
                    <p className="text-zinc-400 text-xs">
                        Whoever leads the leaderboard when the season ends is the champion. There is no mid-season champion-pick bonus in season format.
                    </p>
                )}
            </section>
        </>
    );
}

/* ------------------------------------------------------------------ */
/* 4. Knockout-only (World Cup KO, March Madness, playoffs)            */
/* ------------------------------------------------------------------ */
function KnockoutOnlyRules({ scoring, stageKeys }) {
    return (
        <>
            <section data-testid="rules-knockout-section">
                <p className="text-[10px] uppercase tracking-widest text-cyber font-bold">Knockout bracket</p>
                <h3 className="font-display font-extrabold text-xl mb-1">Win or go home</h3>
                <p className="text-zinc-400 text-xs">
                    <b className="text-cyber">Teams advancing in knockout rounds receive points.</b> Use your Champions Pick — and Power Pick, if enabled — to gain extra points.
                </p>
            </section>

            <section>
                <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-cyber" /> Drafted Team(s) Score
                </h3>
                <ol className="space-y-1 list-decimal list-inside text-zinc-300">
                    <li>Each crewmate ends up with an equal set of teams through the draft (initiated by the Admin).</li>
                    <li>Score grows as those teams progress through the bracket.</li>
                </ol>
            </section>

            <KnockoutBonusList scoring={scoring} stageKeys={stageKeys} />
            <ChampionPickList scoring={scoring} stageKeys={stageKeys} />
        </>
    );
}

/* ------------------------------------------------------------------ */
/* 3. Soccer — Group Stage + Knockout (World Cup, Euros, …)            */
/*    No "Currently supported" header — starts at Drafted Team Score.  */
/* ------------------------------------------------------------------ */
function GroupKnockoutRules({ scoring, stageKeys, tournament }) {
    const hasGroup = stageKeys.has("group");
    const win = scoring.win_points ?? 3;
    const draw = scoring.draw_points ?? 1;
    return (
        <>
            <section data-testid="rules-group-knockout-section">
                <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-cyber" /> Drafted Team(s) Score
                </h3>
                <ol className="space-y-1 list-decimal list-inside text-zinc-300">
                    <li>Each crewmate ends up with an equal set of teams through the draft (initiated by the Admin).</li>
                    <li>Score grows as those teams progress.</li>
                </ol>
            </section>

            {hasGroup && (
                <section>
                    <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                        <Target className="w-4 h-4 text-volt" /> Group stage
                    </h3>
                    <ul className="space-y-1 text-zinc-300" data-testid="rules-scoring-group-list">
                        <li>• Group <b>win</b>: <b className="text-cyber">+{win} pts</b></li>
                        <li>• Group <b>draw</b>: <b className="text-cyber">+{draw} pt{draw === 1 ? "" : "s"}</b></li>
                        <li>• Group <b>loss</b>: <b className="text-cyber">0 pts</b></li>
                    </ul>
                </section>
            )}

            <KnockoutBonusList scoring={scoring} stageKeys={stageKeys} tournament={tournament} />
            <ChampionPickList scoring={scoring} stageKeys={stageKeys} />
        </>
    );
}

/* ------------------------------------------------------------------ */
/* 6. Custom                                                           */
/* ------------------------------------------------------------------ */
function CustomRules({ format, scoring, tournament, stageKeys }) {
    const win = scoring.win_points ?? 0;
    const draw = scoring.draw_points ?? 0;
    const hasGroup = stageKeys.has("group") || stageKeys.has("regular");

    const FORMAT_LABELS = {
        group_knockout: "Group Stage + Knockout",
        knockout_only: "Single-Elimination Bracket",
        round_robin: "Round-Robin",
    };

    return (
        <>
            <section>
                <p className="text-[10px] uppercase tracking-widest text-cyber font-bold">Custom tournament</p>
                <h3 className="font-display font-extrabold text-xl mb-1">{tournament?.name || "Your Tournament"}</h3>
                <p className="text-zinc-400 text-xs">
                    Format: <b className="text-cyber">{FORMAT_LABELS[format] || format}</b>. Points are accumulated with wins, ties and advancing in rounds — the exact values are set by your rivalry admin in <b>Admin Panel → Scoring</b>, so check with your admin to know the scoring system. Match results are recorded manually from <b>Admin Panel → Enter Match</b>.
                </p>
            </section>

            {hasGroup && (format === "group_knockout" || format === "round_robin") && (
                <section>
                    <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                        <Target className="w-4 h-4 text-volt" />
                        {format === "round_robin" ? "Regular season play" : "Group stage"}
                    </h3>
                    <ul className="space-y-1 text-zinc-300" data-testid="rules-scoring-group-list">
                        <li>• <b>Win</b>: <b className="text-cyber">+{win} pts</b></li>
                        <li>• <b>Draw / Tie</b>: <b className="text-cyber">+{draw} pt{draw === 1 ? "" : "s"}</b></li>
                        <li>• <b>Loss</b>: <b className="text-cyber">0 pts</b></li>
                    </ul>
                    {format === "round_robin" && (
                        <p className="text-zinc-500 text-xs mt-2">
                            Every team plays every other team once. Whoever finishes with the most points wins the tournament.
                        </p>
                    )}
                </section>
            )}

            {(format === "group_knockout" || format === "knockout_only") && (
                <>
                    <KnockoutBonusList scoring={scoring} stageKeys={stageKeys} tournament={tournament} />
                    <ChampionPickList scoring={scoring} stageKeys={stageKeys} />
                </>
            )}

            {format === "round_robin" && (
                <section>
                    <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                        <Crown className="w-4 h-4 text-cyber" /> Champion
                    </h3>
                    <p className="text-zinc-400 text-xs">
                        Whoever leads the leaderboard after all matches have been recorded is the champion. No mid-tournament champion-pick bonus applies in round-robin format.
                    </p>
                </section>
            )}

            <section>
                <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-cyber" /> Drafted Team(s) Score
                </h3>
                <ol className="space-y-1 list-decimal list-inside text-zinc-300 text-xs">
                    <li>Each crewmate ends up with an equal set of teams through the draft (initiated by the Admin).</li>
                    <li>Score grows as those teams progress.</li>
                </ol>
            </section>
        </>
    );
}

/* ------------------------------------------------------------------ */
/* Shared: Power Pick section — format-aware copy                      */
/* ------------------------------------------------------------------ */
// iter86.43.2 / iter86.69 — Matchday Boost rules block. `spreadMode`
// (NFL) swaps the winner-pick copy for the Weekly Spread Challenge.
function MatchdayBoostSection({ spreadMode = false }) {
    if (spreadMode) {
        return (
            <section className="border-t-2 border-volt/40 pt-4" data-testid="rules-matchday-boost-section">
                <h3 className="font-display font-extrabold text-base uppercase tracking-tight mb-2 text-volt flex items-center gap-2">
                    ⚡ Weekly Spread Challenge — The Weekly Game
                </h3>
                <p className="text-xs text-zinc-300 leading-relaxed mb-2">
                    Every week, pick up to <b className="text-volt">2 teams against the spread</b> from that week's
                    slate in the <b>Boosts tab</b> — any team, any pot. Graded on the frozen Vegas line:
                </p>
                <div className="grid grid-cols-3 gap-2 text-center text-[11px] mb-2" data-testid="rules-spread-payout-grid">
                    <div className="border-2 border-volt/50 rounded-md p-2">
                        <p className="font-bold text-volt uppercase text-[9px] tracking-wider">Win & cover</p>
                        <p className="font-mono font-bold">+6</p>
                    </div>
                    <div className="border-2 border-cyber/50 rounded-md p-2">
                        <p className="font-bold text-cyber uppercase text-[9px] tracking-wider">Win, no cover</p>
                        <p className="font-mono font-bold">+3</p>
                    </div>
                    <div className="border-2 border-[#333] rounded-md p-2">
                        <p className="font-bold text-zinc-400 uppercase text-[9px] tracking-wider">Lose but cover</p>
                        <p className="font-mono font-bold">+1</p>
                    </div>
                </div>
                <ul className="space-y-1 text-zinc-400 text-xs">
                    <li>• The line <b>freezes ~6h before kickoff</b> (Thu/Sun snapshots) — that frozen number grades your pick. Earlier you'll see a live "~" line that still moves.</li>
                    <li>• Landing exactly on the spread (a push) counts as <b>NOT covered</b>.</li>
                    <li>• Each pick is changeable <b>until its game kicks off</b> — a started pick is locked and counts toward your 2.</li>
                    <li>• Want to plan ahead? The <b>week dropdown</b> in the Boosts tab lets you lock in picks for any future week early.</li>
                    <li>• Skipped a week? No penalty — the game plays on fine.</li>
                    <li>• Banked points show as the "⚡ Matchday Boosts" line in the leaderboard breakdown and count in your total.</li>
                </ul>
            </section>
        );
    }
    return (
        <section className="border-t-2 border-volt/40 pt-4" data-testid="rules-matchday-boost-section">
            <h3 className="font-display font-extrabold text-base uppercase tracking-tight mb-2 text-volt flex items-center gap-2">
                ⚡ Matchday Boost — The Weekly Game
            </h3>
            <p className="text-xs text-zinc-300 leading-relaxed mb-2">
                Every matchday, pick up to <b className="text-volt">2 match winners</b> from that week's
                fixture list in the <b>Boosts tab</b> — any club, any pot. The deeper the pot, the bigger the payout:
            </p>
            <div className="grid grid-cols-3 gap-2 text-center text-[11px] mb-2">
                <div className="border-2 border-[#333] rounded-md p-2">
                    <p className="font-bold text-zinc-400 uppercase text-[9px] tracking-wider">Pot 1 · contenders</p>
                    <p className="font-mono font-bold">W +3 · D +1</p>
                </div>
                <div className="border-2 border-cyber/50 rounded-md p-2">
                    <p className="font-bold text-cyber uppercase text-[9px] tracking-wider">Pot 2 · chasers</p>
                    <p className="font-mono font-bold">W +5 · D +2</p>
                </div>
                <div className="border-2 border-volt/50 rounded-md p-2">
                    <p className="font-bold text-volt uppercase text-[9px] tracking-wider">Pot 3+ · longshots</p>
                    <p className="font-mono font-bold">W +7 · D +3</p>
                </div>
            </div>
            <ul className="space-y-1 text-zinc-400 text-xs">
                <li>• Each pick is changeable <b>until its match kicks off</b> — a started pick is locked and counts toward your 2.</li>
                <li>• Want to plan ahead? The <b>matchweek dropdown</b> in the Boosts tab lets you lock in picks for any future week early.</li>
                <li>• Losses pay nothing. Skipped a week? No penalty — the game plays on fine.</li>
                <li>• Banked boost points show as the "⚡ Matchday Boosts" line in the leaderboard breakdown and count in your total.</li>
            </ul>
        </section>
    );
}

function PowerPickSection({ tournament, golfPickem, golfDraft, isSeason, golfScoring }) {
    const flat = tournament.power_pick_flat_multiplier || 1.5;
    const strokes = golfScoring.power_pick_strokes_per_round ?? 2;
    // iter86.42.2 / iter86.69 — Pot pick'em prices the multiplier by the
    // club's pot; NFL runs 4 pots (Moonshots pay 2.5×).
    const potPickem = tournament.pickem?.mode === "pot";
    const isNflPot = tournament.template === "nfl_season_api";
    const _pmRaw = tournament.pickem?.power_pick_multipliers || {};
    const _pmDefaults = isNflPot ? { 1: 1, 2: 1.25, 3: 1.5, 4: 2 } : { 1: 1, 2: 1.5, 3: 2, 4: 2 };
    const potCount = (tournament.pickem?.pot_rules || []).length || (isNflPot ? 4 : 3);
    const pots = Array.from({ length: potCount }, (_, i) => i + 1);
    const pm = Object.fromEntries(pots.map((p) => [p, Number(_pmRaw[String(p)] ?? _pmDefaults[p] ?? 2)]));
    return (
        <section className="border-t-2 border-volt/40 pt-4" data-testid="rules-power-pick-section">
            <h3 className="font-display font-extrabold text-base uppercase tracking-tight mb-2 text-volt flex items-center gap-2">
                ⚡ Power Pick — Get the Edge
            </h3>
            {golfPickem ? (
                <p className="text-xs text-zinc-300 leading-relaxed" data-testid="rules-power-pick-golf-pickem">
                    Nominate ONE of your golfers for a <span className="text-volt font-bold">standard handicap per round of golf played</span>:
                    <span className="font-mono font-bold"> −{strokes} stroke{strokes > 1 ? "s" : ""}</span> comes off every round they
                    actually play (miss cut = −{strokes * 2}, make cut = −{strokes * 4}). Only counts on YOUR card.
                    Locks at Round 1 tee-off.
                </p>
            ) : golfDraft ? (
                <p className="text-xs text-zinc-300 leading-relaxed" data-testid="rules-power-pick-golf-draft">
                    Select ONE of your drafted golfers to earn <span className="text-volt font-bold">multiplier points over the course of the tournament</span>:
                    every point they earn (make cut + finish position) is multiplied by <span className="font-mono font-bold">{flat}×</span>.
                    Only counts on YOUR card. Locks at Round 1 tee-off.
                </p>
            ) : potPickem ? (
                <p className="text-xs text-zinc-300 leading-relaxed" data-testid="rules-power-pick-pot">
                    Nominate ONE of your {isNflPot ? "teams" : "clubs"} for a <span className="text-volt font-bold">season-long multiplier priced by its pot</span> —
                    {pots.map((p, i) => (
                        <span key={p}>{i > 0 ? ", " : " "}Pot {p} = <span className="font-mono font-bold">{pm[p]}×</span></span>
                    ))}.
                    Every {isNflPot ? "win/tie" : "W/D/L"} point that {isNflPot ? "team" : "club"} earns all season is multiplied — the deeper the pot, the bigger the gamble.
                    Set it before the first {isNflPot ? "game" : "fixture"} kicks off; it locks automatically at kickoff.
                </p>
            ) : isSeason ? (
                <p className="text-xs text-zinc-300 leading-relaxed" data-testid="rules-power-pick-season">
                    Nominate ONE of your drafted teams for a <span className="text-volt font-bold">season-long scoring multiplier</span> —
                    every W/D/L point that team earns all season is multiplied by <span className="font-mono font-bold">{flat}×</span>.
                    Set it before the first fixture kicks off; it locks automatically at kickoff.
                </p>
            ) : (
                <>
                    <p className="text-xs text-zinc-300 leading-relaxed mb-2">
                        Each crewmate nominates ONE of their drafted teams (still alive at the start of knockout) for a <span className="text-volt font-bold">knockout-only scoring multiplier</span>.
                        Set yours on the dashboard before the first knockout round starts — it locks automatically once knockout begins.
                    </p>
                    {tournament.power_pick_mode === "flat" ? (
                        <p className="text-xs text-zinc-300 leading-relaxed">
                            <span className="font-bold text-volt">Flat mode</span> — every crewmate's power team earns <span className="font-mono font-bold">{flat}×</span> the regular advancement points during knockout rounds.
                        </p>
                    ) : (
                        <div className="text-xs text-zinc-300 leading-relaxed space-y-1">
                            <p><span className="font-bold text-volt">Handicap mode</span> — multiplier snapshots at the start of knockout based on your rank:</p>
                            <ul className="ml-3 space-y-0.5">
                                <li>🐢 Bottom 25% (laggards) → <span className="font-mono font-bold">2×</span></li>
                                <li>🚶 Middle 50% → <span className="font-mono font-bold">1.75×</span></li>
                                <li>🐇 Top 25% (leaders) → <span className="font-mono font-bold">1.5×</span></li>
                            </ul>
                            <p className="text-zinc-500 italic">Rivalries with ≤4 crewmates: everyone gets 1.75×.</p>
                        </div>
                    )}
                    <p className="text-[10px] text-zinc-500 italic mt-2">
                        Eligible teams must be drafted by YOU and not yet eliminated. Eliminated teams can't be picked.
                    </p>
                </>
            )}
        </section>
    );
}

function KnockoutBonusList({ scoring, stageKeys, tournament }) {
    const rows = [
        { stage: "r64", label: "Reach Round of 64", value: scoring.reach_r64_points ?? 0, key: "reach_r64_points" },
        { stage: "r32", label: "Reach Round of 32", value: scoring.reach_r32_points ?? 0, key: "reach_r32_points" },
        { stage: "r16", label: "Reach Round of 16", value: scoring.reach_r16_points ?? 0, key: "reach_r16_points" },
        { stage: "qf", label: "Reach Quarterfinal", value: scoring.reach_qf_points ?? 0, key: "reach_qf_points" },
        { stage: "sf", label: "Reach Semifinal", value: scoring.reach_sf_points ?? 0, key: "reach_sf_points" },
        { stage: "final", label: "🥈 Reach Final (silver)", value: scoring.reach_final_points ?? 0, key: "reach_final_points" },
        // iter63z — Bronze row appears IFF the toggle is on. The WS event
        // `tournament_settings_updated` keeps this prop fresh so the row
        // hides/appears immediately on toggle without a page reload.
        ...(tournament?.count_third_place_playoff ? [{
            stage: "_bronze",
            label: "🥉 Win 3rd-place playoff (bronze)",
            value: scoring.bronze_points ?? 0,
            key: "bronze_points",
        }] : []),
        { stage: "champion", label: "🏆 Win the Championship (gold)", value: scoring.champion_points ?? 0, key: "champion_points" },
    ].filter(r => r.stage === "_bronze" || !stageKeys || stageKeys.has(r.stage));
    if (rows.length === 0) return null;
    return (
        <section>
            <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-volt" /> Knockout rounds
            </h3>
            <p className="text-zinc-400 text-xs mb-2">Bonus points when one of your teams advances:</p>
            <ul className="space-y-1 text-zinc-300" data-testid="rules-scoring-list">
                {rows.map(r => (
                    <li key={r.key}>• <b>{r.label}</b>: <b className="text-cyber">+{r.value} pt{r.value === 1 ? "" : "s"}</b></li>
                ))}
            </ul>
        </section>
    );
}

function ChampionPickList({ scoring, stageKeys }) {
    const stages = [
        { key: "bonus_pre_knockout", label: "before any knockout round", value: scoring.bonus_pre_knockout ?? 0, requires: ["r64", "r32", "r16", "qf", "sf", "final"] },
        { key: "bonus_after_r64", label: "after R64 starts", value: scoring.bonus_after_r64 ?? 0, requires: ["r64"] },
        { key: "bonus_after_r32", label: "after R32 starts", value: scoring.bonus_after_r32 ?? 0, requires: ["r32"] },
        { key: "bonus_after_r16", label: "after R16 starts", value: scoring.bonus_after_r16 ?? 0, requires: ["r16"] },
        { key: "bonus_after_qf", label: "after QF starts", value: scoring.bonus_after_qf ?? 0, requires: ["qf"] },
        { key: "bonus_after_sf", label: "after SF starts", value: scoring.bonus_after_sf ?? 0, requires: ["sf"] },
    ].filter(s => !stageKeys || s.requires.some(r => stageKeys.has(r)));
    if (stages.length === 0) return null;
    return (
        <section>
            <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2 mb-2">
                <Crown className="w-4 h-4 text-cyber" /> Champion bonus pick
            </h3>
            <p className="text-zinc-400 text-xs mb-2">
                Pick the eventual champion. Points decay as more rounds start, so commit early.
            </p>
            <ul className="space-y-1 text-zinc-300" data-testid="rules-champion-pick-list">
                {stages.map(s => (
                    <li key={s.key}>• Set <b>{s.label}</b>: <b className="text-cyber">+{s.value} pt{s.value === 1 ? "" : "s"}</b></li>
                ))}
                <li>• Picks <b>lock</b> once the Final begins.</li>
            </ul>
        </section>
    );
}
